/**
 * Optimistic UI Hook for Messages
 * Shows messages instantly, updates on success/failure
 */

import { useCallback } from 'react';
import { Message, Sender } from './types';

export type MessageStatus = 'sending' | 'sent' | 'failed';

export interface OptimisticMessage extends Message {
  status?: MessageStatus;
  error?: string;
}

export function useOptimisticMessages(
  messages: Message[],
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>
) {
  /**
   * Add a message optimistically (shows immediately)
   */
  const addOptimisticMessage = useCallback((text: string): string => {
    const optimisticId = `optimistic_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const optimisticMsg: OptimisticMessage = {
      id: optimisticId,
      text,
      sender: Sender.USER,
      timestamp: new Date(),
      status: 'sending',
    };
    
    setMessages(prev => [...prev, optimisticMsg]);
    return optimisticId;
  }, [setMessages]);

  /**
   * Mark message as successfully sent
   */
  const markMessageSent = useCallback((messageId: string) => {
    setMessages(prev =>
      prev.map(msg =>
        msg.id === messageId
          ? { ...msg, status: 'sent' as MessageStatus }
          : msg
      )
    );
  }, [setMessages]);

  /**
   * Mark message as failed
   */
  const markMessageFailed = useCallback((messageId: string, error?: string) => {
    setMessages(prev =>
      prev.map(msg =>
        msg.id === messageId
          ? { ...msg, status: 'failed' as MessageStatus, error }
          : msg
      )
    );
  }, [setMessages]);

  /**
   * Retry a failed message
   */
  const retryMessage = useCallback((messageId: string) => {
    setMessages(prev =>
      prev.map(msg =>
        msg.id === messageId
          ? { ...msg, status: 'sending' as MessageStatus, error: undefined }
          : msg
      )
    );
  }, [setMessages]);

  /**
   * Remove a message (for failed messages user wants to delete)
   */
  const removeMessage = useCallback((messageId: string) => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
  }, [setMessages]);

  return {
    addOptimisticMessage,
    markMessageSent,
    markMessageFailed,
    retryMessage,
    removeMessage,
  };
}
